import React, { useEffect, useState } from "react";
import { Button, Tooltip } from "antd";
import { CloudOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { useCompany } from "../hooks/useCompany";
import { supabase } from "../lib/supabase";

export const CHANNELDOCK_REGISTER_URL =
  "https://channeldock.com/portal/public/auth/register/e-freight_forwarding";

const Notice = styled.div<{ $collapsed?: boolean }>`
  margin: ${(props) => (props.$collapsed ? "8px" : "8px 12px 12px")};
  padding: ${(props) => (props.$collapsed ? "8px" : "10px 12px")};
  border-radius: 6px;
  background: rgba(250, 173, 20, 0.15);
  border: 1px solid rgba(250, 173, 20, 0.35);
  color: rgba(255, 255, 255, 0.9);
  text-align: ${(props) => (props.$collapsed ? "center" : "left")};
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 1.3;
`;

const Body = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 8px;
`;

interface ChannelDockRegisterSidebarNoticeProps {
  collapsed?: boolean;
}

/**
 * Sidebar prompt when the company uses EFF fulfilment but ChannelDock isn't linked.
 * Shown only if any booking has storage_option = fulfilment_efreight.
 */
export const ChannelDockRegisterSidebarNotice: React.FC<
  ChannelDockRegisterSidebarNoticeProps
> = ({ collapsed = false }) => {
  const { t } = useTranslation();
  const { selectedCompany, companyDetails } = useCompany();
  const [hasFulfilmentBooking, setHasFulfilmentBooking] = useState(false);

  // Wait until details match the selected company (avoids flash on placeholder data).
  const detailsReady =
    !!selectedCompany && companyDetails?.id === selectedCompany;
  const channeldockMissing =
    detailsReady && !companyDetails?.channeldock_enabled;

  useEffect(() => {
    if (!channeldockMissing || selectedCompany == null) {
      setHasFulfilmentBooking(false);
      return;
    }

    let cancelled = false;

    const checkFulfilmentBookings = async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("id")
        .eq("company_id", selectedCompany)
        .eq("storage_option", "fulfilment_efreight")
        .limit(1);

      if (cancelled) return;

      if (error) {
        console.error(
          "Error checking fulfilment_efreight bookings for ChannelDock notice:",
          error
        );
        setHasFulfilmentBooking(false);
        return;
      }

      setHasFulfilmentBooking((data?.length ?? 0) > 0);
    };

    void checkFulfilmentBookings();

    return () => {
      cancelled = true;
    };
  }, [channeldockMissing, selectedCompany]);

  const shouldShow = channeldockMissing && hasFulfilmentBooking;

  if (!shouldShow) return null;

  const openRegister = () => {
    window.open(CHANNELDOCK_REGISTER_URL, "_blank", "noopener,noreferrer");
  };

  if (collapsed) {
    return (
      <Notice $collapsed>
        <Tooltip
          title={t(
            "channeldockRegister.sidebarTitle",
            "Create your ChannelDock account"
          )}
          placement="right"
        >
          <Button
            type="text"
            icon={<CloudOutlined style={{ color: "#faad14" }} />}
            onClick={openRegister}
            aria-label={t(
              "channeldockRegister.createAccount",
              "Create account"
            )}
          />
        </Tooltip>
      </Notice>
    );
  }

  return (
    <Notice>
      <Title>
        {t(
          "channeldockRegister.sidebarTitle",
          "Create your ChannelDock account"
        )}
      </Title>
      <Body>
        {t(
          "channeldockRegister.sidebarDescription",
          "You currently don't have a ChannelDock account, or it isn't linked yet. If you don't have an account yet, create one:"
        )}
      </Body>
      <Button size="small" type="primary" block onClick={openRegister}>
        {t("channeldockRegister.createAccount", "Create account")}
      </Button>
    </Notice>
  );
};
